/*
 * gelato-config.js — Gelato flavour selection config (B2C-239)
 *
 * Single source of truth for the gelato flavour catalogs and the SKU -> {max, catalog}
 * map. Data + pure lookups ONLY: no DOM, no fetch, no events (see decisions/gelato-flavours.md Q1).
 *
 * Loaded with `defer`; assigns a namespaced global `window.CasinettoGelato`. The modal
 * controller (gelato.js) and the product-card state machine consume it.
 *
 * Allergens are normalised arrays of strings, parsed by hand at authoring time from the
 * spec appendix display strings ("Soy, Milk, Eggs" -> ['Soy','Milk','Eggs'], "None" -> []).
 * An empty array means "No allergens" (the modal renders the positive pill). Never parse
 * at runtime.
 *
 * Post-launch migration (decisions Q1): the export surface (getConfig/isGelato) is the
 * stable seam — swap the data source to metaobjects behind it without touching callers.
 */
(function () {
  'use strict';

  var MIN_FLAVOURS = 1; // hard-coded, not per-SKU (spec §1)

  // Spec Appendix A — 20 flavours. "Pistachio" spelling corrected from source.
  var flavoursList = [
    { name: 'Stracciatella', allergens: ['Milk'], description: 'A perfect combination of cream & high-quality crunchy chocolate chunks' },
    { name: 'Vanilla Bourbon', allergens: ['Milk'], description: 'A tantalising blend of smooth vanilla & bold bourbon flavour' },
    { name: 'Milk Chocolate', allergens: ['Milk'], description: 'A smooth and creamy classic crafted with the finest chocolate and cacao powder' },
    { name: 'Dark Chocolate', allergens: ['Soy', 'Milk', 'Eggs'], description: 'A luxurious treat for chocolate lovers made using the finest cacao powder' },
    { name: 'Caffè "Coppa del Nonno"', allergens: ['Milk'], description: 'A rich and creamy flavour that combines the bold and aromatic taste of 100% Arabica coffee with a smooth texture' },
    { name: 'Pistacchio di Bronte', allergens: ['Milk', 'Nuts'], description: 'An exquisite gelato flavour, made with high-quality, DOP-certified Pistachios from the slopes of Mount Etna' },
    { name: 'Cremino', allergens: ['Milk', 'Nuts'], description: 'A creamy IGP Tonda Gentile Hazelnut gelato with a rich layer of hazelnut cream' },
    { name: 'Lotus', allergens: ['Milk'], description: 'Crushed Lotus Biscoff cookies and a caramelised flavour make for a sweet and indulgent treat' },
    { name: 'Amalfi Lemon', allergens: [], description: 'A refreshing & zesty burst of flavour made exclusively of fresh lemons imported from the Amalfi coast' },
    { name: 'Sicilian Orange', allergens: [], description: 'A taste of sunshine in every bite made exclusively of fresh oranges imported from Sicily' },
    { name: 'Mango Alphonso', allergens: [], description: 'Enjoy the king of mangoes as a sweet and refreshing treat with a tropical twist, made exclusively from fresh mangoes' },
    { name: 'Strawberry', allergens: [], description: 'An explosion of flavour and freshness in a smooth sorbet texture, made exclusively from fresh strawberries' },
    { name: 'Yuzu', allergens: [], description: 'A rare and refreshing Japanese fruit similar to lemon, prized for its aroma and a sought-after super fruit for its health benefits - all packed in a unique gelato flavour.' },
    { name: "Amira D'oriente Tahina", allergens: ['Dairy', 'Sesame'], description: 'A blend of oriental flavours with Humera sesame from Ethiopia, paired with Medjool date syrup for a unique, delicate, and delicious flavour combination with a crunchy texture.' },
    { name: 'Pistachio Pesto with Cervia Salt', allergens: ['Milk', 'Nuts'], description: 'A rich combination of premium pistachios with the prized Cervia Salt, a sweet whole sea salt also known as "the salt of the Pope".' },
    { name: 'Hazelnut Pesto with Fleur de sel', allergens: ['Gluten', 'Soy', 'Milk', 'Sesame', 'Nuts'], description: 'A luscious paste of large crunchy hazelnuts complemented with Fleur de sel, a rare and prized salt from the Camargue region of France, enhancing the delicious nut base.' },
    { name: 'Kefir', allergens: ['Dairy', 'Eggs', 'Soy'], description: 'A delicately flavoured frozen treat unlike yogurt, derived from ancient fermented origins with a less acidic and more delicate flavour.' },
    { name: 'Cheesecake', allergens: ['Dairy', 'Soy', 'Eggs', 'Wheat'], description: 'A delightful contrast of sweet biscuits and the characteristic saltiness of cheesecake captures the sandy texture and flavour of this iconic American dessert.' },
    { name: 'Ricotta Fig', allergens: ['Dairy'], description: 'A creamy ricotta with the sweet, earthy notes of figs for a delightful and unique frozen dessert experience.' },
    { name: 'Pina Colada', allergens: [], description: 'The tropical flavours of coconut and pineapple create a creamy and refreshing dessert reminiscent of the classic cocktail.' }
  ];

  // Spec Appendix B — 10 flavours.
  var flavoursList142 = [
    { name: 'Strawberry milk', allergens: ['Milk'], description: 'Sweet and creamy with the comforting taste of fresh strawberries.' },
    { name: 'Coconut', allergens: ['Milk'], description: 'Light and refreshing, with the smooth essence of coconut in every bite.' },
    { name: 'Milk Chocolate', allergens: ['Milk'], description: 'Rich and velvety, offering a chocolate experience that feels just right.' },
    { name: 'Hazelnut', allergens: ['Milk', 'Hazelnut'], description: 'Warm and nutty with a hint of toasted hazelnuts, perfect for every mood.' },
    { name: 'Pistachio', allergens: ['Milk', 'Nuts'], description: 'A creamy blend with the distinct, natural flavour of pistachios.' },
    { name: 'Caramel', allergens: ['Milk'], description: 'Smooth and golden, with a gentle sweetness that lingers just enough.' },
    { name: 'Vanilla', allergens: ['Milk'], description: 'Pure and mellow, the kind of flavour that feels like home.' },
    { name: 'Raspberry sorbet', allergens: [], description: 'Bright and tangy, with a bold kick of raspberries to refresh.' },
    { name: 'Lemon sorbet', allergens: ['Soya'], description: 'Crisp and zesty, delivering a clean citrus flavour with every spoonful.' },
    { name: 'Mango sorbet', allergens: [], description: 'Sweet and tropical, capturing the juicy essence of ripe mangoes.' }
  ];

  // SKU map — keys are NORMALISED base SKUs (suffix already stripped). Spec §1 table.
  // Only these 4 boxes are flavour-configurable. (GELF531/GELF532 are NOT flavour
  // gelatos — they fell through to a default max in the legacy system.)
  var skuMap = {
    GELF110: { max: 2, catalog: flavoursList },
    GELF111: { max: 3, catalog: flavoursList },
    GELF142: { max: 3, catalog: flavoursList142 },
    GELF520: { max: 4, catalog: flavoursList }
  };

  // GELF110_1 -> GELF110. Suffix stripping is logic, stays in JS post-migration.
  function normaliseSku(sku) {
    if (!sku) {
      return '';
    }
    return String(sku).replace(/_\d+$/, '');
  }

  // (sku) -> { max, min, catalog } | null. `catalog` is returned by reference — treat as
  // read-only (the controller builds a filtered copy for search; never sort/filter in place).
  function getGelatoConfig(sku) {
    var cfg = skuMap[normaliseSku(sku)];
    if (!cfg) {
      return null;
    }
    return { max: cfg.max, min: MIN_FLAVOURS, catalog: cfg.catalog };
  }

  function isGelato(sku) {
    return getGelatoConfig(sku) !== null;
  }

  window.CasinettoGelato = {
    getConfig: getGelatoConfig, // (sku) -> { max, min, catalog } | null
    isGelato: isGelato, // (sku) -> bool
    normaliseSku: normaliseSku,
    MIN: MIN_FLAVOURS
  };
})();
